import { type MouseEventHandler, useId, useMemo, useState } from "react";
import { cardStyles } from "../core/Card.styles.js";
import { ChipRadioGroup } from "../core/Chip.js";
import { Text } from "../core/Text.js";
import { cn } from "../foundations/cn.js";
import { type DropItemCardProps, ItemCard } from "./ItemCard.js";
import { OddsBar } from "./OddsBar.js";
import { OddsTable, type OddsTableRow } from "./OddsTable.js";
import type { TierMarker } from "./OddsTag.js";
import type { RarityTier } from "./rarity.js";

export type DropSort = "value" | "odds";

export interface DropTableProps {
  /** The section's heading: "What's inside". */
  title: string;
  /** Beside the title: "48 items". */
  count?: string;
  /** The sort group's name, and each sort's words. */
  sortLabel: string;
  sorts: { value: { label: string }; odds: { label: string } };
  /** Each tier's published chance in percent, in tier order. */
  odds: readonly { tier: RarityTier; chance: number; label?: string }[];
  /** Every item the box can hold. */
  drops: readonly (Omit<DropItemCardProps, "variant" | "onSelect" | "className"> & {
    id: string;
  })[];
  /**
   * How the odds by tier show above the items: `bar` (OddsBar), `table` (OddsTable, with each
   * tier's value range taken from its items) or `none`.
   */
  oddsView?: "bar" | "table" | "none";
  /** How each item's tier shows beside its chance: the tier's dot, or its shape. */
  marker?: TierMarker;
  /**
   * What the whole table asks the shopper to know (a limited supply, an item that can no longer
   * ship), under the items. Shown in full; never folded away.
   */
  notes?: readonly string[];
  /** Opens one item's details; without it the cards are static. */
  onSelectDrop?: (id: string, event: Parameters<MouseEventHandler<HTMLButtonElement>>[0]) => void;
  /** Cards per row at the widest: 4 beside a purchase column, 6 full width. */
  columns?: 4 | 6;
  className?: string;
}

/** Each tier's value range from the items in it, with the tier's published chance. */
function tierRows(odds: DropTableProps["odds"], drops: DropTableProps["drops"]): OddsTableRow[] {
  return [...odds].reverse().map((tier) => {
    const values = drops.filter((d) => d.tier === tier.tier).map((d) => d.value);
    return {
      tier: tier.tier,
      label: tier.label,
      chance: tier.chance,
      min: values.length ? Math.min(...values) : undefined,
      max: values.length ? Math.max(...values) : undefined,
    };
  });
}

/**
 * Everything a box can hold, with its published odds: the heading, a sort by value or chance,
 * the odds by tier in the chosen view, and a card per item. A compliance surface: every item and
 * its chance always show.
 */
export function DropTable({
  title,
  count,
  sortLabel,
  sorts,
  odds,
  drops,
  oddsView = "bar",
  marker,
  notes = [],
  onSelectDrop,
  columns = 4,
  className,
}: DropTableProps) {
  const titleId = useId();
  const [sort, setSort] = useState<DropSort>("value");
  const sorted = useMemo(
    () => [...drops].sort((a, b) => (sort === "value" ? b.value - a.value : b.chance - a.chance)),
    [drops, sort],
  );
  return (
    <section aria-labelledby={titleId} className={cn("flex flex-col gap-5", className)}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-baseline gap-3">
          <Text
            as="h2"
            id={titleId}
            variant="display-xl2"
            tone="primary"
            className="max-md:text-display-xl3"
          >
            {title}
          </Text>
          {count && (
            <Text variant="body-b2" tone="tertiary">
              {count}
            </Text>
          )}
        </div>
        <ChipRadioGroup
          label={sortLabel}
          value={sort}
          onValueChange={setSort}
          options={[
            { value: "value", label: sorts.value.label },
            { value: "odds", label: sorts.odds.label },
          ]}
        />
      </div>
      {oddsView === "bar" && <OddsBar tiers={odds} />}
      {oddsView === "table" && <OddsTable rows={tierRows(odds, drops)} />}
      <ul
        className={cn(
          "grid grid-cols-2 gap-3 sm:grid-cols-3",
          columns === 6 ? "lg:grid-cols-5 xl:grid-cols-6" : "xl:grid-cols-4",
        )}
      >
        {sorted.map(({ id, ...drop }) => (
          <li key={id}>
            <ItemCard
              variant="drop"
              marker={marker}
              {...drop}
              onSelect={onSelectDrop ? (event) => onSelectDrop(id, event) : undefined}
            />
          </li>
        ))}
      </ul>
      {notes.length > 0 && (
        <div className="flex flex-col gap-2">
          {notes.map((note) => (
            <Text
              key={note}
              as="p"
              variant="body-b3"
              tone="secondary"
              className={cardStyles({ radius: "lg", className: "px-4 py-3" })}
            >
              {note}
            </Text>
          ))}
        </div>
      )}
    </section>
  );
}
